import { useId, useRef, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { SPRING_SWITCH, SWITCH } from '../tokens/motion'
import { cn } from '../lib/cn'

export interface TabOption<T extends string> {
  value: T
  label: string
}

/**
 * Peer views of one page, as words on a hairline: the chosen word inks and
 * a thin underline glides beneath it on the switch spring. No pill, no
 * sunken box — the page is already the container. Pair it with
 * `SwitchPanel` so the view it governs hands over by the same rule.
 */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T
  options: readonly TabOption<T>[]
  onChange: (value: T) => void
  label: string
  className?: string
}) {
  const group = useId()
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  return (
    <div role="tablist" aria-label={label} className={cn('flex items-end gap-6 border-b border-hairline', className)}>
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            id={`${group}-tab-${option.value}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            ref={(node) => { buttons.current[index] = node }}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
              if (!direction && event.key !== 'Home' && event.key !== 'End') return
              event.preventDefault()
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
                : (index + direction + options.length) % options.length
              onChange(options[next]!.value)
              buttons.current[next]?.focus()
            }}
            className={cn(
              'relative -mb-px pb-2.5 text-ui font-medium outline-none transition-colors duration-base',
              'focus-visible:text-foreground focus-visible:underline focus-visible:underline-offset-4',
              selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
            {selected ? (
              <motion.span
                layoutId={`${group}-underline`}
                transition={SPRING_SWITCH}
                className="absolute inset-x-0 bottom-0 h-[1.5px] rounded-full bg-foreground"
              />
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The view a switch governs. Changing `activeKey` hands it over by the one
 * switching rule: the old view dissolves, then the new one rises and comes
 * into focus — never both at once.
 */
export function SwitchPanel({ activeKey, children, className }: { activeKey: string; children: ReactNode; className?: string }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={activeKey} {...SWITCH} className={className}>
        {children}
      </motion.div>
    </AnimatePresence>
  )
}
